import { fg } from '@atlaskit/platform-feature-flags';
import type { Perimeter } from '@atlassian/atl-context';
import { ExperiencePerformanceTypes, ExperienceTypes, UFOExperience } from '@atlassian/ufo';

import { type CategorizedStorageControlsCache, CategoryStorageType } from '../../../types';
import { Logger } from '../logger';

import {
	CATEGORY_CACHE_URL_PERIMETER_LOOKUP,
	COMMERCIAL_CATEGORY_CACHE_URL_LOOKUP,
	COOKIE_CACHE_URL_COMMERCIAL_PROD,
	LEGACY_COOKIE_CACHE_URL_PROD,
	LOCAL_STORAGE_CACHE_URL_COMMERCIAL_PROD,
	SESSION_STORAGE_CACHE_URL_COMMERCIAL_PROD,
} from './constants';

export class CacheLoader {
	private cacheUrl: string;

	/**
	 * Cache variable for storing categorized cookies cache
	 */
	private localResponseCache: CategorizedStorageControlsCache | undefined;

	/**
	 * Cache variable for storing a promise that's been fired by a different invocation of loadStorageControls
	 *
	 * Allows for many invocations to happen simultaneously, and for only one fetch to go out
	 */
	private localResponsePromiseCache: Promise<Response> | undefined;

	/**
	 * Cache variable for storing the promise returned from the response object's json method
	 *
	 * Ensures we don't invoke response.json multiple times
	 */
	private localResponseJsonPromiseCache:
		| Promise<CategorizedStorageControlsCache | undefined>
		| undefined;

	constructor(storageType: CategoryStorageType, perimeter: Perimeter = 'commercial') {
		if (!fg('fedramp-cookies-controls')) {
			switch (storageType) {
				case CategoryStorageType.Cookies:
					this.cacheUrl = COOKIE_CACHE_URL_COMMERCIAL_PROD;
					break;
				case CategoryStorageType.LocalStorage:
					this.cacheUrl = LOCAL_STORAGE_CACHE_URL_COMMERCIAL_PROD;
					break;
				case CategoryStorageType.SessionStorage:
					this.cacheUrl = SESSION_STORAGE_CACHE_URL_COMMERCIAL_PROD;
					break;
				default:
					this.cacheUrl = LEGACY_COOKIE_CACHE_URL_PROD;
			}
		} else {
			// If a new perimeter type is added, but not supported by us yet, default to commercial URLs
			const cacheUrlLookup =
				CATEGORY_CACHE_URL_PERIMETER_LOOKUP[perimeter] || COMMERCIAL_CATEGORY_CACHE_URL_LOOKUP;
			this.cacheUrl = cacheUrlLookup[storageType];
		}
	}

	clearStorageControlsDataCache() {
		this.localResponseCache = undefined;
		this.localResponsePromiseCache = undefined;
		this.localResponseJsonPromiseCache = undefined;
	}

	/**
	 * Unpacks the response promise from the categories CDN
	 *
	 * errors if that request was unsuccessful, returns the categories data otherwise
	 *
	 */
	private async handleCategoriesResponse(responsePromise: Promise<Response> | undefined) {
		if (!responsePromise) {
			throw new Error(`Failed to fetch Cookies Cache! Cached promise was undefined`);
		}

		const response = await responsePromise;
		if (!response.ok) {
			throw new Error(`Failed to fetch Cookies Cache! status: ${response.status}`);
		}
		// Checks if we've already fired the response.json() promise
		this.localResponseJsonPromiseCache = this.localResponseJsonPromiseCache || response?.json();

		return this.localResponseJsonPromiseCache;
	}

	async loadStorageControlsData(): Promise<CategorizedStorageControlsCache | undefined> {
		const isFetchTrackingSampled = !Math.floor(Math.random() * 100);
		const shouldTrackFetch = fg('platform_measure_bsc_fetch_time') && isFetchTrackingSampled;
		let fetchExperience: UFOExperience | null = null;

		if (shouldTrackFetch) {
			fetchExperience = new UFOExperience('browser-storage-controls-fetch-time', {
				type: ExperienceTypes.Load,
				performanceType: ExperiencePerformanceTypes.Custom,
				platform: { component: 'browser-storage-controls' },
			});
			fetchExperience.start();
		}

		if (this.localResponseCache) {
			if (fetchExperience) {
				fetchExperience.success();
			}
			return this.localResponseCache;
		} else {
			// Always use Prod CDN for prod/local/dev/staging environments, but stub the route for automation tests
			try {
				if (!this.localResponsePromiseCache) {
					Logger.debug(`Loading cache ${this.cacheUrl}`);
					// invoke CDN and cache the promise
					this.localResponsePromiseCache = fetch(this.cacheUrl);
				}

				// unpack response
				this.localResponseCache = await this.handleCategoriesResponse(
					this.localResponsePromiseCache,
				);

				if (fetchExperience) {
					fetchExperience.success();
				}

				return this.localResponseCache;
			} catch (e: any) {
				if (fetchExperience) {
					fetchExperience.failure({ metadata: { error: String(e) } });
				}
				Logger.errorWithOperationalEvent({
					action: 'loadStorageControlsError',
					message: e.message || '',
				});
				this.clearStorageControlsDataCache();
			}
		}
	}
}

export {
	CATEGORY_CACHE_URL_PERIMETER_LOOKUP,
	COMMERCIAL_CATEGORY_CACHE_URL_LOOKUP,
	LEGACY_COOKIE_CACHE_URL_PROD,
} from './constants';
